function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeList(values) {
  if (!Array.isArray(values)) return [];
  return values.map((v) => normalizeText(v)).filter(Boolean);
}

function includesValue(haystack, needle) {
  const n = normalizeText(needle);
  if (!n) return false;
  return normalizeList(haystack).some((item) => item.includes(n) || n.includes(item));
}

export function buildDiagnosisProfile(userProfile = {}, user = {}) {
  return {
    username: normalizeText(user?.username || userProfile?.username),
    cancerType: normalizeText(userProfile?.cancerType),
    subtype: normalizeText(userProfile?.cancerSubtype || userProfile?.subtype),
    stage: normalizeText(userProfile?.diseaseStage || userProfile?.stage),
    biomarkers: normalizeList(userProfile?.biomarkers || userProfile?.genomics),
    locationPreference: normalizeText(userProfile?.cancerCenter || userProfile?.city || ''),
  };
}

export function getTrialMatch(trial, diagnosisProfile = {}) {
  const reasons = [];
  let score = 0;

  const trialCancerType = normalizeText(trial?.cancerType);
  const trialSubtype = normalizeText(trial?.subtype);
  const trialStage = normalizeText(trial?.stage);
  const trialBiomarkers = normalizeList(trial?.biomarkers);
  const trialLocation = `${normalizeText(trial?.city)} ${normalizeText(trial?.region)} ${normalizeText(trial?.country)} ${normalizeText(trial?.institution)}`;

  if (diagnosisProfile.cancerType && trialCancerType) {
    if (trialCancerType === diagnosisProfile.cancerType) {
      score += 4;
      reasons.push('Cancer type matches your diagnosis.');
    }
  }

  if (diagnosisProfile.subtype && trialSubtype) {
    if (trialSubtype === diagnosisProfile.subtype) {
      score += 2;
      reasons.push('Subtype aligns with your profile.');
    }
  }

  if (diagnosisProfile.stage && trialStage) {
    if (trialStage.includes(diagnosisProfile.stage) || diagnosisProfile.stage.includes(trialStage)) {
      score += 3;
      reasons.push('Trial stage criteria may fit your current stage.');
    }
  }

  if (diagnosisProfile.biomarkers.length > 0 && trialBiomarkers.length > 0) {
    const matchedBiomarkers = diagnosisProfile.biomarkers.filter((marker) => includesValue(trialBiomarkers, marker));
    if (matchedBiomarkers.length > 0) {
      score += 3;
      reasons.push(`Biomarker overlap: ${matchedBiomarkers.join(', ').toUpperCase()}.`);
    }
  }

  if (diagnosisProfile.locationPreference && trialLocation.includes(diagnosisProfile.locationPreference)) {
    score += 1;
    reasons.push('Location aligns with your care preferences.');
  }

  const isMatched = score >= 4;
  return {
    isMatched,
    matchScore: score,
    matchReasons: reasons,
  };
}

export function applyDiagnosisMatching(trials, diagnosisProfile) {
  return (Array.isArray(trials) ? trials : [])
    .map((trial) => {
      const match = getTrialMatch(trial, diagnosisProfile);
      return {
        ...trial,
        isMatched: match.isMatched,
        matchScore: match.matchScore,
        matchReasons: match.matchReasons,
      };
    })
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
}
